import {Component, forwardRef, Input, OnDestroy, Output, ViewChild, ViewEncapsulation, EventEmitter} from '@angular/core';
import {ControlValueAccessor, UntypedFormBuilder, UntypedFormControl, NG_VALUE_ACCESSOR} from '@angular/forms';
import {EntiteModel} from '../../../../models/entites/entite.model';
import {EntitesService} from '../../../../services/entites/entites.service';
import {Subscription} from 'rxjs';
import {FormFieldComponent} from '../mise-en-page/form-field/form-field.component';
import { MultiSelectChangeEventArgs } from '@syncfusion/ej2-angular-dropdowns';

@Component({
  selector: 'app-select-entites-old',
  templateUrl: './select-entites.component.html',
  styleUrls: ['./select-entites.component.scss'],
  encapsulation: ViewEncapsulation.None,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SelectEntitesComponent),
      multi: true,
    },
  ],
})
export class SelectEntitesComponent implements ControlValueAccessor, OnDestroy {

  private subs: Subscription[] = [];
  entitesUser: EntiteModel[] = [];

  @ViewChild(FormFieldComponent) formField: FormFieldComponent;

  @ViewChild('ejsMultiselect') ejsMultiselect;
  private _filter: number[];
  private _multiple = true;

  @Input() set multiple(value: boolean) {
    this._multiple = value;
  }

  @Input() set filter(value: number[]) {
    this._filter = value;
    this.resetDefaultValueIfNecessary();
  }

  @Input() disabled = false;
  @Input() hideTitle = false;

  @Output() onChangeValue = new EventEmitter();

  get title(): string {
    return !this.hideTitle ? 'Entités' : '';
  }

  isCustomValueAllowed = true;

  private _entitesSelected: number[] | number;

  formControl: UntypedFormControl;

  public static copyValue(value: number[] | number) {
    if (Array.isArray(value)) {
      return [...value];
    } else {
      return value;
    }
  }

  public static map(from: EntiteModel): { [key: string]: Object } {
    return { "id": from.id, "libelle": from.libelle};
  }

  private onChanged = _ => {};
  private onTouched = () => {};

  private areValuesEqual(value1: number | number[], value2: number | number[]) {
    if (!Array.isArray(value1)) {
      return value1 !== value2;
    }
    const [v1, v2] = [value1 as number[], value2 as number[]];
    return v1 != null && v2 != null && v1.length == v2.length && v1.every(value => v2.indexOf(value) >= 0);
  }

  private resetDefaultValueIfNecessary() {
    if (!this.hasFilter || !this.someEntitesAreNotInFilter || this.entitesSelected == null)
    {
      return;
    }

    const valeursDefaut = this.selectionUnionFilter;

    if (this.isSelection && valeursDefaut.length > 0) {
      this.entitesSelected = this.multiple ? valeursDefaut : valeursDefaut[0];
    }
    else {
      this.entitesSelected = this.multiple ? [this.filter[0]] : this.filter[0];
    }
  }

  constructor(private formBuilder: UntypedFormBuilder, private entitesService: EntitesService) {
    const atLeastOneValidator = (c: UntypedFormControl) => {
      if (c.value != null && c.value.length == 0) {
        return {atLeastOne: true};
      } else {
        return null;
      }
    };

    this.formControl = formBuilder.control(null, [atLeastOneValidator]);

    this.subs.push(this.formControl.valueChanges.subscribe(value => {
      this.entitesSelected = value;
    }));

    this.subs.push(this.entitesService.getUtilisateursEntites().subscribe(entites => {
      this.entitesUser = [...entites];

      if (!this.entites.length || this.entitesSelected != null) {
        return;
      }

      const selection = this.entites[0]['id'] as number;

      if (this.multiple) {
        this.entitesSelected = [selection];
      } else {
        this.entitesSelected = selection;
      }
    }));
  }

  ngOnDestroy(): void {
    this.subs.forEach(it => it.unsubscribe());
    this.subs = [];
  }

  get hasFilter(): boolean {
    return this.filter != null && this.filter.length > 0;
  }

  get someEntitesAreNotInFilter(): boolean {
    if (!this.hasFilter || this.entitesSelected == null) {
      return false;
    }

    if (this.multiple) {
      return (this.entitesSelected as number[]).some(idEntite => !(this.filter.indexOf(idEntite) >= 0));
    } else {
      return this.filter.indexOf(this.entitesSelected as number) >= 0;
    }
  }

  get multiple(): boolean {
    return this._multiple;
  }
  get filter(): number[] {
    return this._filter;
  }

  set entitesSelected(value: number[] | number) {
    value = SelectEntitesComponent.copyValue(value);

    if (value === this._entitesSelected || this.areValuesEqual(value, this._entitesSelected)) {
      return;
    }

    this._entitesSelected = value;
    this.formControl.setValue(value);
    this.onChanged(value);
  }

  get entitesSelected(): number[] | number {
    return this._entitesSelected;
  }

  get isSelection(): boolean {

    if (this.entitesSelected == null) {
      return false;
    }

    if (this.multiple) {
      return (this.entitesSelected as number[]).length > 0;
    }

    return true;
  }

  get selectionUnionFilter(): number[] {

    if (!this.hasFilter) {
      return [];
    }

    if (this.multiple) {
      return (this.entitesSelected as number[]).filter(it1 => this.filter.indexOf(it1) >= 0);
    }

    return this.filter.indexOf(this.entitesSelected as number) > 0 ? [this.entitesSelected as number] : [];
  }

  get entites(): { [key: string]: Object }[] {

    let entites: EntiteModel[];

    if (this.filter != null && this.filter.length > 0) {
      entites = this.entitesUser.filter(e => this.filter.find(item => item == e.id));
    } else {
      entites = this.entitesUser;
    }

    return entites != null ? entites.map(SelectEntitesComponent.map) : [];
  }

  get isEntitesHidden(): boolean {
    return this.entites.length <= 1;
  }

  registerOnChange(fn: any): void {
    this.onChanged = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.formControl.disable();
    } else {
      this.formControl.enable();
    }
  }

  writeValue(obj: number[] | number): void {
    if (obj == null) {
      this.entitesSelected = null;
      return;
    }

    if (this.multiple == true) {
      if (!Array.isArray(obj)) {
        this.entitesSelected = [obj];
      } else {
        this.entitesSelected = obj;
      }
    } else if (Array.isArray(obj)) {
      this.entitesSelected = obj[0];
    } else {
      this.entitesSelected = obj;
    }
  }

  setAllowCustomValueToFalse() {
    if (this.ejsMultiselect != null) {
      this.ejsMultiselect.allowCustomValue = false;
      this.ejsMultiselect.refresh();
    }
  }

  emitChange($event: Event) {
    this.onChangeValue.emit(true);
  }
}
